import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

// ── Static data ──
const CITIES = [
  'Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide',
  'Gold Coast', 'Cairns', 'Canberra', 'Newcastle', 'Wollongong',
  'Hobart', 'Darwin', 'Townsville', 'Geelong', 'Ballarat',
  'Bendigo', 'Toowoomba', 'Launceston', 'Mackay', 'Rockhampton',
  'Bunbury', 'Hervey Bay', 'Wagga Wagga', 'Coffs Harbour',
  'Sunshine Coast', 'Alice Springs', 'Albury', 'Mildura', 'Shepparton',
];

const NICHES = [
  'cosmetic clinic', 'medspa', 'skin clinic', 'laser clinic', 'aesthetic clinic',
  'cafe', 'restaurant', 'hair salon', 'beauty salon', 'plumber',
  'electrician', 'dentist', 'physiotherapist', 'real estate agent',
  'car dealership', 'law firm',
];

// Canonical service names used everywhere
const SERVICES = [
  'Google Review Agent',
  'Website Development',
  'Chatbot (Carl)',
  'Automated Admin Assistant',
];

const TONALITY_OPTIONS = [
  'Human and conversational',
  'Direct and confident',
  'Casual Australian',
  'Professional but warm',
];

const FREE_MODELS = [
  'stepfun/step-3.5-flash:free',
  'google/gemma-3-27b-it:free',
  'meta-llama/llama-3.3-70b-instruct:free',
  'qwen/qwen3-coder:free',
  'google/gemma-3-12b-it:free',
  'nvidia/nemotron-nano-9b-v2:free',
];

const SERVICE_DESCRIPTIONS = {
  'Google Review Agent': 'a fully automated system that sends personalised review requests after every job, follows up automatically, and uses AI to reply to every Google review — runs on autopilot with zero manual input',
  'Website Development': 'a custom website built to convert visitors into real enquiries — handcoded, mobile-responsive, and deployed to their own domain. They own it outright',
  'Chatbot (Carl)': 'Carl, a custom AI sales assistant that lives on their website 24/7, qualifies leads, collects contact details, and pushes visitors toward booking a call — never identified as AI',
  'Automated Admin Assistant': 'a done-for-you automation of repetitive admin tasks — follow-up emails, appointment reminders, lead responses and review requests. Built on Google Workspace, no ongoing SaaS fees',
};

const EMAIL_SUBJECTS = {
  'Website Development': 'your online presence',
  'Chatbot (Carl)': 'missing leads after hours?',
  'Automated Admin Assistant': 'automating the repetitive stuff',
  'Google Review Agent': 'your Google reviews',
};

const DEFAULT_CALL_SCRIPTS = {
  'Google Review Agent': "Hey, just following up on an email sent through recently about your Google reviews. Noticed the review count was on the lower side — just wanted to see if you'd had a chance to look at it and if it's something worth having a quick chat about.",
  'Website Development': "Hey, reaching out about an email sent through about your online presence. Had a look at your setup online and noticed a gap that could be costing you leads — wanted to see if you'd be open to a quick 15-minute chat.",
  'Chatbot (Carl)': "Hey, following up on an email about capturing leads after hours. A lot of businesses in your space are losing enquiries overnight — wanted to see if that's something you'd want to fix.",
  'Automated Admin Assistant': "Hey, sent through an email about automating some of the repetitive admin work. Just wanted to see if you'd had a chance to look at it and whether it's worth a quick chat.",
};

const DEFAULT_EMAIL_SCRIPT = `Open with something noticed about the business that hints at the problem. Second paragraph briefly introduces what we do without over-explaining. Third paragraph is a subtle social proof or result. Close with a soft ask for a 15 minute call. Never mention price. Never name the business or location.`;

// ── Existing clients to seed ──
const EXISTING_CLIENTS = [
  {
    business_name: 'HaiRoom',
    owner_name: 'Inna',
    email: 'hairoomwithinna@gmail.com',
    service_gap: 'Google Review Agent + Website',
    status: 'Active Client',
    notes: 'Existing client',
  },
  {
    business_name: 'Ocean Jetty Cafe',
    service_gap: 'Website Development',
    status: 'Active Client',
    notes: 'Existing client',
  },
  {
    business_name: 'Raye of Hair',
    service_gap: 'Website Development',
    status: 'Active Client',
    notes: 'Existing client',
  },
];

// ── Row mappers ──
function mapProspect(row) {
  return {
    id: row.id,
    businessName: row.business_name,
    ownerName: row.owner_name,
    email: row.email,
    phone: row.phone,
    suburb: row.suburb,
    city: row.city,
    industry: row.industry,
    serviceGap: row.service_gap,
    notes: row.notes,
    status: row.status,
    lastEmailedAt: row.last_emailed_at,
    followUpCount: row.follow_up_count,
    createdAt: row.created_at,
    replied: row.replied || false,
    called: row.called || false,
    isHot: row.is_hot || false,
  };
}

function mapLead(row) {
  return {
    id: row.id,
    name: row.business_name,
    email: row.email,
    ownerName: row.owner_name,
    phone: row.phone,
    address: row.address,
    suburb: row.address || '',
    city: row.city,
    niche: row.niche,
    rating: row.rating,
    reviewCount: row.review_count,
    website: row.website,
    suggestedService: row.suggested_service,
    serviceReason: row.service_reason,
    isHot: row.is_hot,
    scrapedAt: row.scraped_at,
  };
}

function mapSettings(row) {
  if (!row) {
    return {
      openrouterApiKey: '',
      appsScriptUrl: '',
      senderName: 'Zac @ ZJ Marketing',
      dailyEmailCap: 50,
      followUpDelayDays: 5,
      maxFollowUps: 2,
      requireApproval: true,
      tonality: 'Human and conversational',
      emailScript: DEFAULT_EMAIL_SCRIPT,
      callScriptGoogleReview: DEFAULT_CALL_SCRIPTS['Google Review Agent'],
      callScriptWebsite: DEFAULT_CALL_SCRIPTS['Website Development'],
      callScriptChatbot: DEFAULT_CALL_SCRIPTS['Chatbot (Carl)'],
      callScriptAdmin: DEFAULT_CALL_SCRIPTS['Automated Admin Assistant'],
    };
  }
  return {
    openrouterApiKey: row.openrouter_api_key || '',
    appsScriptUrl: row.apps_script_url || '',
    senderName: row.sender_name || 'Zac @ ZJ Marketing',
    dailyEmailCap: row.daily_email_cap || 50,
    followUpDelayDays: row.follow_up_delay_days || 5,
    maxFollowUps: row.max_follow_ups || 2,
    requireApproval: row.require_approval !== false,
    tonality: row.tonality || 'Human and conversational',
    emailScript: row.email_script || DEFAULT_EMAIL_SCRIPT,
    callScriptGoogleReview: row.call_script_google_review || DEFAULT_CALL_SCRIPTS['Google Review Agent'],
    callScriptWebsite: row.call_script_website || DEFAULT_CALL_SCRIPTS['Website Development'],
    callScriptChatbot: row.call_script_chatbot || DEFAULT_CALL_SCRIPTS['Chatbot (Carl)'],
    callScriptAdmin: row.call_script_admin || DEFAULT_CALL_SCRIPTS['Automated Admin Assistant'],
  };
}

// ── Internal helpers ──
async function fetchSettings() {
  const { data, error } = await supabase.from('settings').select('*').limit(1).maybeSingle();
  if (error) throw new Error(error.message);
  return mapSettings(data);
}

async function aiGenerate(prompt, apiKey) {
  let lastError;
  for (const model of FREE_MODELS) {
    try {
      const res = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': 'https://zjmarketing.com',
          'X-Title': 'ZJ Outreach System',
        },
        body: JSON.stringify({ model, messages: [{ role: 'user', content: prompt }] }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error?.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      return data.choices[0].message.content;
    } catch (e) {
      lastError = e;
    }
  }
  throw lastError || new Error('All AI models failed');
}

async function sendViaAppsScript(appsScriptUrl, payload) {
  const res = await fetch(appsScriptUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify(payload),
    redirect: 'follow',
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data;
}

function getTonalityInstructions(tonality) {
  switch (tonality) {
    case 'Direct and confident':
      return 'Tone: direct and confident. Get straight to the point. No fluff, no softening language. Assertive but not pushy.';
    case 'Casual Australian':
      return 'Tone: casual Australian. Use natural, relaxed Australian English. Contractions are fine. Sound like a mate who noticed a problem and wants to help sort it out.';
    case 'Professional but warm':
      return 'Tone: professional but warm. Polished and clear, but not cold or corporate. Feel approachable and genuine.';
    case 'Human and conversational':
    default:
      return 'Tone: human and conversational. Sound like a real person who did a bit of research and noticed something. Not salesy, not corporate. Like a genuine conversation.';
  }
}

function buildEmailPrompt(prospect, isFollowUp, settings = {}) {
  const serviceDesc = SERVICE_DESCRIPTIONS[prospect.serviceGap] || prospect.serviceGap;
  const tonalityLine = getTonalityInstructions(settings.tonality || 'Human and conversational');
  const emailScript = settings.emailScript || DEFAULT_EMAIL_SCRIPT;

  if (isFollowUp) {
    return `Write a short, direct follow-up cold email for a business outreach campaign.

Context:
- Business: ${prospect.businessName}
- Industry: ${prospect.industry}
- We previously emailed about: ${serviceDesc}

Rules:
- Start with "Hi" — do NOT use their name, do NOT say "Hi there" or "Dear business owner"
- This is a follow-up to a previous email they didn't reply to
- Keep it very short — 2-3 paragraphs max
- ${tonalityLine}
- Reference the previous email naturally
- NEVER mention price
- NEVER mention the business name in the email body
- NEVER mention their location, suburb, or city
- NEVER mention our location or say local
- NEVER say "business proposal"
- NEVER start a sentence with "I"
- NO bullet points
- End with a direct ask for a quick 15 minute call
- Sign off as just "Zac" from ZJ Marketing
- No subject line needed — just the email body
- Write like a real Australian person who noticed something wrong with their business`;
  }

  return `Write a personalised cold email for a business outreach campaign.

Context:
- Business: ${prospect.businessName}
- Industry: ${prospect.industry}
- Service we want to suggest: ${serviceDesc}

Email framework to follow:
${emailScript}

Hard Rules — these are non-negotiable:
- Start with "Hi" — do NOT use their name, do NOT say "Hi there" or "Dear business owner"
- NEVER mention the business name in the email body
- NEVER mention their location, suburb, or city
- NEVER mention our location, city, or say "local"
- NEVER mention price
- NEVER say "business proposal"
- NEVER start a sentence with "I"
- NO bullet points
- NO corporate language
- NEVER identify as AI
- ${tonalityLine}
- Short sentences
- Maximum 5 short paragraphs
- End with a soft ask for a 15 minute call
- Sign off as just "Zac" from ZJ Marketing
- Sound like a real Australian person who noticed a specific gap in this business and genuinely wants to help fix it`;
}

function getCorrectSubject(serviceGap) {
  return EMAIL_SUBJECTS[serviceGap] || serviceGap || 'quick question';
}

async function countEmailsSentToday() {
  const today = new Date().toISOString().split('T')[0];
  const { count } = await supabase
    .from('email_log')
    .select('*', { count: 'exact', head: true })
    .gte('sent_at', `${today}T00:00:00.000Z`);
  return count || 0;
}

// ── After hours check (9am-5pm AEST = UTC+10) ──
function isAfterHoursAEST(isoString) {
  if (!isoString) return false;
  const date = new Date(isoString);
  // Convert to AEST (UTC+10)
  const aestHour = (date.getUTCHours() + 10) % 24;
  return aestHour < 9 || aestHour >= 17;
}

// ── Public API ──
export const api = {
  // Dashboard — full rebuild with all new metrics
  getDashboard: async () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const weekAgo = new Date(now);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

    const [
      { data: leads },
      { data: prospects },
      { data: emailLog },
      settings,
    ] = await Promise.all([
      supabase.from('leads').select('id, scraped_at, is_hot'),
      supabase.from('prospects').select('*'),
      supabase.from('email_log').select('id, sent_at, type, business_name, subject, prospect_id, to_email'),
      fetchSettings(),
    ]);

    const allLeads = leads || [];
    const allProspects = prospects || [];
    const allEmails = emailLog || [];

    const followUpDelay = settings.followUpDelayDays || 5;
    const maxFollowUps = settings.maxFollowUps || 2;
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - followUpDelay);

    // Weekly chart
    const weekDays = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];
      weekDays.push({
        day: d.toLocaleDateString('en-AU', { weekday: 'short' }),
        count: allEmails.filter(e => e.sent_at && e.sent_at.startsWith(dateStr)).length,
      });
    }

    const pendingFollowUps = allProspects.filter(p =>
      (p.status === 'Emailed' || p.status === 'Follow Up') &&
      p.last_emailed_at &&
      new Date(p.last_emailed_at) <= cutoff &&
      (p.follow_up_count || 0) < maxFollowUps
    );

    const emailsThisMonth = allEmails.filter(e => e.sent_at && e.sent_at >= monthStart);
    const emailsSentMonth = emailsThisMonth.length;
    const emailsSentToday = allEmails.filter(e => e.sent_at && e.sent_at.startsWith(today)).length;
    const emailsSentWeek = allEmails.filter(e => e.sent_at && new Date(e.sent_at) >= weekAgo).length;

    // After hours count this month
    const afterHoursMonth = emailsThisMonth.filter(e => isAfterHoursAEST(e.sent_at)).length;

    // Reply rate
    const totalEmailed = allProspects.filter(p => p.last_emailed_at).length;
    const totalReplied = allProspects.filter(p => p.replied === true).length;
    const repliedThisMonth = allProspects.filter(p =>
      p.replied === true && p.last_emailed_at && p.last_emailed_at >= monthStart
    ).length;
    const replyRate = totalEmailed > 0 ? ((totalReplied / totalEmailed) * 100).toFixed(1) : '0.0';

    // Hours saved this month
    const hoursSaved = ((emailsSentMonth * 8) / 60).toFixed(1);
    const labourCost = Math.round((emailsSentMonth * 8 / 60) * 35);

    const hotLeads = allLeads.filter(l => l.is_hot).length;
    const calledCount = allProspects.filter(p => p.called === true).length;
    const bookedCount = allProspects.filter(p => p.status === 'Booked').length;

    const recentEmails = [...allEmails]
      .sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at))
      .slice(0, 10)
      .map(e => ({
        businessName: e.business_name,
        subject: e.subject,
        sentAt: e.sent_at,
        type: e.type,
        toEmail: e.to_email,
        afterHours: isAfterHoursAEST(e.sent_at),
      }));

    return {
      totalLeads: allLeads.length,
      leadsScrapedToday: allLeads.filter(l => l.scraped_at && l.scraped_at.startsWith(today)).length,
      emailsSentToday,
      emailsSentWeek,
      emailsSentMonth,
      replyRate,
      totalReplied,
      repliedThisMonth,
      hotLeads,
      calledCount,
      bookedCount,
      followUpsPending: pendingFollowUps.length,
      hoursSaved,
      labourCost,
      afterHoursMonth,
      emailsPerDay: weekDays,
      recentEmails,
      dailyCap: settings.dailyEmailCap || 50,
      followUpReminders: pendingFollowUps.map(p => ({
        id: p.id,
        businessName: p.business_name,
        email: p.email,
        serviceGap: p.service_gap,
        lastEmailedAt: p.last_emailed_at,
        daysSince: Math.floor((now - new Date(p.last_emailed_at)) / (1000 * 60 * 60 * 24)),
        followUpCount: p.follow_up_count || 0,
      })),
    };
  },

  // Leads
  getLeads: async () => {
    const { data, error } = await supabase.from('leads').select('*').order('scraped_at', { ascending: false });
    if (error) throw new Error(error.message);
    return (data || []).map(mapLead);
  },
  deleteLead: async (id) => {
    const { error } = await supabase.from('leads').delete().eq('id', id);
    if (error) throw new Error(error.message);
    return { success: true };
  },
  pushToProspect: async (leadId) => {
    const { data: lead, error } = await supabase.from('leads').select('*').eq('id', leadId).single();
    if (error) throw new Error(error.message);

    const { data: existing } = await supabase
      .from('prospects')
      .select('id')
      .ilike('business_name', lead.business_name)
      .maybeSingle();
    if (existing) throw new Error('Prospect already exists');

    const { error: insertError } = await supabase.from('prospects').insert({
      business_name: lead.business_name,
      owner_name: lead.owner_name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      suburb: lead.suburb || '',
      city: lead.city || '',
      industry: lead.niche || '',
      service_gap: lead.suggested_service || '',
      status: 'New',
      notes: `Rating: ${lead.rating || 'N/A'} | Reviews: ${lead.review_count} | Website: ${lead.website}`,
      follow_up_count: 0,
      is_hot: lead.is_hot || false,
    });
    if (insertError) throw new Error(insertError.message);
    return { success: true };
  },
  pushAllToProspects: async () => {
    const { data: leads, error: leadsError } = await supabase.from('leads').select('*');
    if (leadsError) throw new Error(leadsError.message);

    const { data: existing } = await supabase.from('prospects').select('business_name');
    const existingNames = new Set((existing || []).map(p => p.business_name.toLowerCase()));

    const toInsert = (leads || [])
      .filter(l => !existingNames.has((l.business_name || '').toLowerCase()))
      .map(l => ({
        business_name: l.business_name,
        owner_name: l.owner_name || '',
        email: l.email || '',
        phone: l.phone || '',
        suburb: l.suburb || '',
        city: l.city || '',
        industry: l.niche || '',
        service_gap: l.suggested_service || '',
        status: 'New',
        notes: `Rating: ${l.rating || 'N/A'} | Reviews: ${l.review_count} | Website: ${l.website}`,
        follow_up_count: 0,
        is_hot: l.is_hot || false,
      }));

    if (toInsert.length > 0) {
      const { error: insertError } = await supabase.from('prospects').insert(toInsert);
      if (insertError) throw new Error(insertError.message);
    }

    const { count } = await supabase.from('prospects').select('*', { count: 'exact', head: true });
    return { pushed: toInsert.length, total: count || 0 };
  },

  // Prospects
  getProspects: async () => {
    const { data, error } = await supabase.from('prospects').select('*').order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return (data || []).map(mapProspect);
  },
  addProspect: async (prospect) => {
    const { error } = await supabase.from('prospects').insert({
      business_name: prospect.businessName || '',
      owner_name: prospect.ownerName || '',
      email: prospect.email || '',
      phone: prospect.phone || '',
      suburb: prospect.suburb || '',
      city: prospect.city || '',
      industry: prospect.industry || '',
      service_gap: prospect.serviceGap || '',
      notes: prospect.notes || '',
      status: 'New',
      follow_up_count: 0,
    });
    if (error) throw new Error(error.message);
    return { success: true };
  },
  updateProspect: async (id, updates) => {
    const dbUpdates = {};
    if (updates.businessName !== undefined) dbUpdates.business_name = updates.businessName;
    if (updates.ownerName !== undefined) dbUpdates.owner_name = updates.ownerName;
    if (updates.email !== undefined) dbUpdates.email = updates.email;
    if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
    if (updates.suburb !== undefined) dbUpdates.suburb = updates.suburb;
    if (updates.city !== undefined) dbUpdates.city = updates.city;
    if (updates.industry !== undefined) dbUpdates.industry = updates.industry;
    if (updates.serviceGap !== undefined) dbUpdates.service_gap = updates.serviceGap;
    if (updates.notes !== undefined) dbUpdates.notes = updates.notes;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.lastEmailedAt !== undefined) dbUpdates.last_emailed_at = updates.lastEmailedAt;
    if (updates.followUpCount !== undefined) dbUpdates.follow_up_count = updates.followUpCount;
    if (updates.replied !== undefined) dbUpdates.replied = updates.replied;
    if (updates.called !== undefined) dbUpdates.called = updates.called;
    const { error } = await supabase.from('prospects').update(dbUpdates).eq('id', id);
    if (error) throw new Error(error.message);
    return { success: true };
  },
  deleteProspect: async (id) => {
    const { error } = await supabase.from('prospects').delete().eq('id', id);
    if (error) throw new Error(error.message);
    return { success: true };
  },

  // Scraper — calls Railway scraper server
  scrape: async (cities, niches) => {
    const scraperUrl = process.env.REACT_APP_SCRAPER_URL;
    if (!scraperUrl) throw new Error('Scraper not configured. Set REACT_APP_SCRAPER_URL.');
    const res = await fetch(`${scraperUrl}/scrape`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cities, niches }),
    });
    const data = await res.json();
    if (!res.ok || data.error) throw new Error(data.error || 'Scraper failed');
    return { newCount: data.newCount, totalCount: data.newCount };
  },

  // Static data
  getCities: async () => CITIES,
  getNiches: async () => NICHES,
  getServices: async () => SERVICES,
  getTonalityOptions: async () => TONALITY_OPTIONS,
  getEmailSubjects: () => EMAIL_SUBJECTS,
  getCorrectSubject,

  // Generate email via OpenRouter
  generateEmail: async (prospectId, isFollowUp = false) => {
    const [settings, { data: row, error }] = await Promise.all([
      fetchSettings(),
      supabase.from('prospects').select('*').eq('id', prospectId).single(),
    ]);
    if (error) throw new Error(error.message);
    if (!settings.openrouterApiKey) throw new Error('OpenRouter API key not configured. Add it in Settings.');

    const prospect = mapProspect(row);
    const body = await aiGenerate(buildEmailPrompt(prospect, isFollowUp, settings), settings.openrouterApiKey);
    const subject = getCorrectSubject(prospect.serviceGap);
    return { subject, body, prospectId: prospect.id };
  },

  // Generate custom email with custom prompt
  generateCustomEmail: async (prospectId, customContext) => {
    const [settings, { data: row, error }] = await Promise.all([
      fetchSettings(),
      supabase.from('prospects').select('*').eq('id', prospectId).single(),
    ]);
    if (error) throw new Error(error.message);
    if (!settings.openrouterApiKey) throw new Error('OpenRouter API key not configured. Add it in Settings.');

    const prospect = mapProspect(row);
    const serviceDesc = SERVICE_DESCRIPTIONS[prospect.serviceGap] || prospect.serviceGap;
    const tonalityLine = getTonalityInstructions(settings.tonality || 'Human and conversational');

    const prompt = `Write a personalised cold email for a business outreach campaign.

Context:
${customContext || `Business: ${prospect.businessName}\nIndustry: ${prospect.industry}\nService: ${serviceDesc}`}

Hard Rules:
- Start with "Hi"
- NEVER mention the business name in the email body
- NEVER mention location
- NEVER mention price
- NEVER say "business proposal"
- NEVER start a sentence with "I"
- NO bullet points
- ${tonalityLine}
- Maximum 5 short paragraphs
- End with a soft ask for a 15 minute call
- Sign off as just "Zac" from ZJ Marketing`;

    const body = await aiGenerate(prompt, settings.openrouterApiKey);
    const subject = getCorrectSubject(prospect.serviceGap);
    return { subject, body, prospectId: prospect.id };
  },

  // Send email via Apps Script, log to Supabase
  sendEmail: async (prospectId, subject, body) => {
    const [settings, { data: row, error }] = await Promise.all([
      fetchSettings(),
      supabase.from('prospects').select('*').eq('id', prospectId).single(),
    ]);
    if (error) throw new Error(error.message);
    if (!settings.appsScriptUrl) throw new Error('Apps Script URL not configured. Add it in Settings.');
    if (!row.email) throw new Error('Prospect has no email address.');

    const sentToday = await countEmailsSentToday();
    if (sentToday >= settings.dailyEmailCap) {
      throw new Error(`Daily email cap reached (${settings.dailyEmailCap}). Try again tomorrow.`);
    }

    const emailType = (row.status === 'Emailed' || row.status === 'Follow Up') ? 'follow-up' : 'initial';

    await sendViaAppsScript(settings.appsScriptUrl, {
      action: 'send-email',
      to: row.email,
      subject,
      body,
      businessName: row.business_name || '',
      type: emailType,
      senderName: settings.senderName,
    });

    await supabase.from('email_log').insert({
      prospect_id: prospectId,
      business_name: row.business_name,
      to_email: row.email,
      subject,
      body,
      type: emailType,
      sent_at: new Date().toISOString(),
    });

    const newFollowUpCount = emailType === 'follow-up' ? (row.follow_up_count || 0) + 1 : row.follow_up_count || 0;
    let newStatus;
    if (emailType === 'follow-up') {
      newStatus = newFollowUpCount >= settings.maxFollowUps ? 'Cold' : 'Follow Up';
    } else {
      newStatus = 'Emailed';
    }
    await supabase.from('prospects').update({
      status: newStatus,
      last_emailed_at: new Date().toISOString(),
      follow_up_count: newFollowUpCount,
    }).eq('id', prospectId);

    return { success: true, sentToday: sentToday + 1, dailyCap: settings.dailyEmailCap };
  },

  // Email log
  getEmailLog: async () => {
    const { data, error } = await supabase
      .from('email_log')
      .select('*, prospects(follow_up_count, replied)')
      .order('sent_at', { ascending: false });
    if (error) throw new Error(error.message);
    return (data || []).map(e => ({
      id: e.id,
      prospectId: e.prospect_id,
      businessName: e.business_name,
      to: e.to_email,
      subject: e.subject,
      body: e.body,
      type: e.type,
      sentAt: e.sent_at,
      afterHours: isAfterHoursAEST(e.sent_at),
      followUpSent: (e.prospects?.follow_up_count || 0) > 0,
      replied: e.prospects?.replied || false,
    }));
  },

  // Check follow-ups due
  checkFollowUps: async () => {
    const settings = await fetchSettings();
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - (settings.followUpDelayDays || 5));

    const { data, error } = await supabase
      .from('prospects')
      .select('*')
      .in('status', ['Emailed', 'Follow Up'])
      .lte('last_emailed_at', cutoff.toISOString())
      .lt('follow_up_count', settings.maxFollowUps || 2);
    if (error) throw new Error(error.message);
    return { pending: (data || []).map(mapProspect) };
  },

  // Send emails to all new prospects
  sendAllEmails: async () => {
    const settings = await fetchSettings();
    if (!settings.appsScriptUrl) throw new Error('Apps Script URL not configured.');
    if (!settings.openrouterApiKey) throw new Error('OpenRouter API key not configured.');

    const sentToday = await countEmailsSentToday();
    const { data: prospects, error } = await supabase
      .from('prospects')
      .select('*')
      .eq('status', 'New')
      .not('email', 'is', null)
      .neq('email', '');
    if (error) throw new Error(error.message);
    if (!prospects || prospects.length === 0) return { success: true, sent: 0, total: 0, errors: [] };

    let sent = 0;
    const errors = [];

    for (const row of prospects) {
      if (sentToday + sent >= settings.dailyEmailCap) {
        errors.push(`Daily cap reached after ${sent} emails.`);
        break;
      }
      try {
        const prospect = mapProspect(row);
        const emailBody = await aiGenerate(buildEmailPrompt(prospect, false, settings), settings.openrouterApiKey);
        const subject = getCorrectSubject(prospect.serviceGap);

        await sendViaAppsScript(settings.appsScriptUrl, {
          action: 'send-email',
          to: row.email,
          subject,
          body: emailBody,
          businessName: row.business_name || '',
          type: 'initial',
          senderName: settings.senderName,
        });

        await supabase.from('email_log').insert({
          prospect_id: row.id,
          business_name: row.business_name,
          to_email: row.email,
          subject,
          body: emailBody,
          type: 'initial',
          sent_at: new Date().toISOString(),
        });

        await supabase.from('prospects').update({
          status: 'Emailed',
          last_emailed_at: new Date().toISOString(),
        }).eq('id', row.id);

        sent++;
        if (sent < prospects.length) await new Promise(r => setTimeout(r, 3000));
      } catch (err) {
        errors.push(`${row.business_name}: ${err.message}`);
      }
    }

    return { success: true, sent, total: prospects.length, errors };
  },

  // Settings
  getSettings: fetchSettings,
  saveSettings: async (settings) => {
    const { data: existing } = await supabase.from('settings').select('id').limit(1).maybeSingle();
    const basePayload = {
      openrouter_api_key: settings.openrouterApiKey || '',
      apps_script_url: settings.appsScriptUrl || '',
      sender_name: settings.senderName || 'Zac @ ZJ Marketing',
      daily_email_cap: settings.dailyEmailCap || 50,
      follow_up_delay_days: settings.followUpDelayDays || 5,
      max_follow_ups: settings.maxFollowUps || 2,
      require_approval: settings.requireApproval !== false,
    };
    // Extended fields — wrapped in try/catch in case columns don't exist yet
    const extendedPayload = {};
    try {
      extendedPayload.tonality = settings.tonality || 'Human and conversational';
      extendedPayload.email_script = settings.emailScript || DEFAULT_EMAIL_SCRIPT;
      extendedPayload.call_script_google_review = settings.callScriptGoogleReview || DEFAULT_CALL_SCRIPTS['Google Review Agent'];
      extendedPayload.call_script_website = settings.callScriptWebsite || DEFAULT_CALL_SCRIPTS['Website Development'];
      extendedPayload.call_script_chatbot = settings.callScriptChatbot || DEFAULT_CALL_SCRIPTS['Chatbot (Carl)'];
      extendedPayload.call_script_admin = settings.callScriptAdmin || DEFAULT_CALL_SCRIPTS['Automated Admin Assistant'];
    } catch (e) {
      // columns may not exist
    }
    const payload = { ...basePayload, ...extendedPayload };
    let error;
    if (existing) {
      ({ error } = await supabase.from('settings').update(payload).eq('id', existing.id));
    } else {
      ({ error } = await supabase.from('settings').insert(payload));
    }
    if (error) throw new Error(error.message);
    return { success: true };
  },

  // Seed existing clients
  seedExistingClients: async () => {
    const { data: existing } = await supabase.from('prospects').select('business_name');
    const existingNames = new Set((existing || []).map(p => p.business_name.toLowerCase()));

    const toInsert = EXISTING_CLIENTS.filter(
      c => !existingNames.has(c.business_name.toLowerCase())
    );

    if (toInsert.length > 0) {
      const { error } = await supabase.from('prospects').insert(
        toInsert.map(c => ({
          business_name: c.business_name,
          owner_name: c.owner_name || '',
          email: c.email || '',
          phone: c.phone || '',
          suburb: '',
          city: '',
          industry: '',
          service_gap: c.service_gap,
          status: c.status,
          notes: c.notes,
          follow_up_count: 0,
        }))
      );
      if (error) console.error('Seed clients error:', error.message);
    }
    return { seeded: toInsert.length };
  },

  // Expose constants
  SERVICES,
  CITIES,
  NICHES,
  TONALITY_OPTIONS,
  EMAIL_SUBJECTS,
  DEFAULT_CALL_SCRIPTS,
  DEFAULT_EMAIL_SCRIPT,
  SERVICE_DESCRIPTIONS,
  isAfterHoursAEST,
};
